import * as React from 'react'

import { Button, useToggle } from '@factorialco/deprecated-design-system'

import Popover from 'modules/core/components/Popover'
import type { Option } from 'modules/core/components/Popover/Content'
import PopoverContent from 'modules/core/components/Popover/Content'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'
import AccessGroup from 'modules/core/store/models/AccessGroup'
import type User from 'modules/core/store/models/User'

import { RevieweesContext } from 'modules/reviews/lib/RevieweesContext'
import formAccessGroupMemberships from 'modules/reviews/store/collections/formAccessGroupMemberships'

function useAccessGroupMembership(
  accessGroup: AccessGroup | null | undefined,
  user: User,
  formId: string
) {
  const [membership, setMembership] = React.useState(
    formAccessGroupMemberships.find({
      access_id: user.attributes.id,
      access_group_id: accessGroup?.attributes.id,
    })
  )
  useUnsafeAsyncData(async () => {
    if (membership) return

    if (!accessGroup) return

    if (accessGroup.attributes.strategy !== AccessGroup.Strategies.members) return

    await formAccessGroupMemberships.fetch({
      data: {
        form_id: formId,
        access_group_id: accessGroup?.attributes.id,
      },
      remove: false,
    })
    setMembership(
      formAccessGroupMemberships.find({
        access_id: user.attributes.id,
        access_group_id: accessGroup.attributes.id,
      })
    )
  }, [accessGroup])

  return {
    membership,
  }
}

interface Props {
  accessGroup: AccessGroup | null | undefined
  user: User
}

export default function Settings({ user, accessGroup }: Props) {
  const [open, toggle] = useToggle()
  const { fetchReviewees, reviewModel } = React.useContext(RevieweesContext)
  const { membership } = useAccessGroupMembership(accessGroup, user, reviewModel?.id)

  const { performAction: destroyMembership } = useAsyncAction(async () => {
    if (!membership) return

    await membership.destroy({ data: { form_id: reviewModel?.id } })
    fetchReviewees()
  })

  if (!membership) return null

  if (!accessGroup) return null

  const options: Option[] = [
    {
      title: i18n.t('surveys.review.participants.table.body.remove_peer'),
      type: 'wrong',
      onClick: destroyMembership,
    },
  ]

  return (
    <Popover attachment="top right" targetAttachment="bottom center" toggle={toggle} open={open}>
      <Button onClick={toggle(true)} type="secondary" iconName="ellipsis" />
      <PopoverContent onClose={toggle(false)} options={options} />
    </Popover>
  )
}
